import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { takeLatest, call, put, all, select } from "redux-saga/effects";

//Custom Imports
import { contestType } from "@reducers/actionTypes";
import contestAction from "@actions/contest.action";
import {
  pastContest,
  contestDetails,
  upcomingAndLive,
  myCustomers,
  contestCxOrdered,
} from "@services/api";
const PATH = "screens/Contest";
import color from "@constants/color";
import t from "@constants/localization";
import { LIVE, UPCOMING } from "@constants/contestTypes";
import {
  myCustomersSelector,
  pastContestSelector,
  contestDetailsSelector,
  liveUpcomingDataSelector,
  contestCxOrderedSelector,
  moreCustLoadingSelector,
  moreOrderedCustLoadingSelector,
} from "@selectors/contest.selector";
import { getTime } from "@utils/getHindiDate";

const PATH_DETAILS = "screens/Contest/contestDetails";
dayjs.extend(duration);

function* getLiveUpcoming() {
  try {
    const { api = {} } = yield select(liveUpcomingDataSelector);
    const { fetched = false } = api || {};
    const { data } = yield call(upcomingAndLive, { limit: 50, offset: 0 });
    if (Array.isArray(data)) {
      const liveData = [],
        upcomingData = [],
        finalData = [];
      const current_time = dayjs(new Date());
      data.forEach((element) =>
        current_time.isBefore(element?.start_time)
          ? upcomingData.push(element)
          : liveData.push(element),
      );
      liveData.length !== 0 &&
        finalData.push({
          title: t(`${PATH}/live`),
          icon: LIVE,
          data: liveData,
          type: "Live",
        });
      upcomingData.length !== 0 &&
        finalData.push({
          title: t(`${PATH}/upcoming`),
          icon: UPCOMING,
          data: upcomingData,
          type: "Upcoming",
        });
      yield put(contestAction.getLiveUpcomingContest.success(finalData, null));
      if (!fetched)
        yield put(contestAction.getLiveUpcomingContest.fetched({}, null));
    }
  } catch (err) {
    yield put(contestAction.getLiveUpcomingContest.error({ err }, null));
    // yield call(Logger.error, err);
  }
}

function* getPastContestData() {
  try {
    const { api = {} } = yield select(pastContestSelector);
    const { fetched = false } = api || {};
    const { data } = yield call(pastContest, { limit: 50, offset: 0 });
    yield put(contestAction.getPastContest.success(data, null));
    if (!fetched) yield put(contestAction.getPastContest.fetched({}, null));
  } catch (err) {
    yield put(contestAction.getPastContest.error({ err }, null));
    // yield call(Logger.error, err);
  }
}

const getProgressPayload = (
  iconName,
  current_slab_index,
  left_progress_name,
  right_progress_name,
  low,
  payout,
) => {
  const obj = {
    icon: iconName,
    current_slab_index,
    left_side_data: [
      {
        name: left_progress_name,
        count: low,
        horizontal: true,
        isNameBold: false,
        color: color.mediumBlack,
      },
    ],
    right_side_data: [
      {
        name: right_progress_name,
        count: payout,
        horizontal: false,
        isNameBold: false,
        color: color.mediumBlack,
      },
    ],
  };
  return obj;
};

const getFormattedSlabs = (
  slabs,
  current_slab_index,
  ordered_cx,
  contest_ended,
) => {
  const formatted_slabs = [];
  formatted_slabs.push({
    icon: "startProgressIcon",
    current_slab_index,
    right_side_data: [
      {
        name: t(`${PATH_DETAILS}/start`),
        horizontal: false,
        isNameBold: true,
        count: "",
      },
    ],
  });

  slabs.forEach((item, index) => {
    const { low, payout } = item;
    if (
      current_slab_index === slabs.length - 1 &&
      current_slab_index === index &&
      contest_ended
    ) {
      formatted_slabs.push(
        getProgressPayload(
          "finalProgressIcon",
          current_slab_index,
          t(`${PATH_DETAILS}/totalCustomersMade`),
          t(`${PATH_DETAILS}/contestEnded`),
          low,
          payout,
        ),
      );
    } else if (current_slab_index > index) {
      formatted_slabs.push(
        getProgressPayload(
          "trophyProgressIcon",
          current_slab_index,
          t(`${PATH_DETAILS}/newCustomers`),
          t(`${PATH_DETAILS}/bonusEarned`),
          low,
          payout,
        ),
      );
    } else if (current_slab_index === index) {
      formatted_slabs.push(
        getProgressPayload(
          "profileIcon",
          current_slab_index,
          t(`${PATH_DETAILS}/newCustomers`),
          t(`${PATH_DETAILS}/youAreHere`),
          low,
          payout,
        ),
      );
    } else {
      formatted_slabs.push(
        getProgressPayload(
          slabs.length - 1 === index ? "finalProgressIcon" : "lockProgressIcon",
          current_slab_index,
          t(`${PATH_DETAILS}/newCustomers`),
          "",
          low,
          payout,
        ),
      );
    }
  });

  const currentSlab = slabs[current_slab_index];
  if (contest_ended) {
    formatted_slabs.splice(
      current_slab_index + 1,
      1,
      getProgressPayload(
        "profileIcon",
        current_slab_index,
        t(`${PATH_DETAILS}/totalCustomersMade`),
        t(`${PATH_DETAILS}/contestEnded`),
        ordered_cx,
        currentSlab?.payout ?? 0,
      ),
    );
  }

  return formatted_slabs;
};

function* getContestDetails({ payload }) {
  try {
    const { contest_id } = payload;
    const { data } = yield call(contestDetails, { contest_id });
    const { api = {} } = yield select(contestDetailsSelector);
    const { fetched = false } = api || {};
    const {
        contest = {},
        ordered_cx = 0,
        total_cx = 0,
        contest_result_viewed = false,
        last_processed_time = null,
        performance = "",
        can_only_view_banner = "",
        can_only_view = false,
        tl_id = "",
        payout_done = false,
        payout_transaction_id = null,
        payout_amount = 0,
      } = data,
      list_items = [],
      current_slab_index =
        data?.current_slab_index === -1 ? 0 : data?.current_slab_index;
    if (contest.contest_banner) {
      list_items.push({
        type: "CONTEST_BANNER",
        item: {
          contest_banner: contest.contest_banner,
          title: contest.contest_name,
        },
      });
    }
    if (can_only_view && tl_id) {
      list_items.push({
        type: "ELIGIBILITY_BANNER",
        item: {
          can_only_view_banner,
          can_only_view,
          tl_id,
        },
      });
    }
    if (contest?.show_task_on_top) {
      if (contest.contest_rules_cms_item_id) {
        list_items.push({
          type: "CONTEST_RULES",
          item: {
            contest_rules_cms_item_id: contest.contest_rules_cms_item_id,
            contest_type: contest.contest_type,
          },
        });
      }
      if (contest.tips_banner) {
        list_items.push({
          type: "TIPS_BANNER",
          item: {
            tips_banner: contest.tips_banner,
          },
        });
      }
    }
    if (contest?.contest_ended) {
      list_items.push({
        type: "CONTEST_UPDATE",
        item: {
          payout_time: contest.payout_time,
          payout_type: contest.payout_type,
          payouts_by_rank: contest.payouts_by_rank,
          payout_done,
          payout_transaction_id,
          payout_amount,
        },
      });
    }
    if (total_cx) {
      list_items.push({
        type: "YOUR_TASK",
        item: {
          ordered_cx: ordered_cx,
          total_cx: total_cx,
          current_slab_index: current_slab_index,
          contest_type: contest.contest_type,
        },
      });
    }
    if (contest?.contest_ended) {
      const myJourenyData = contest.slabs[current_slab_index];
      list_items.push({
        type: "MY_PERFORMANCE",
        item: {
          current_slab_index: current_slab_index,
          slabs: contest.slabs,
          sku_ids: contest.sku_ids,
          rewards_earned: myJourenyData?.payout ?? 0,
          customers_made: ordered_cx,
          level_reached: current_slab_index + 1,
          contest_ended: contest.contest_ended,
          contest_result_viewed: contest_result_viewed,
          performance: performance,
        },
      });
    }
    if (contest?.contest_ended) {
      list_items.push({
        type: "ORDERS_PLACED_CARD",
        item: {
          contest_ended: contest.contest_ended,
          total_cx,
          ordered_cx,
          contest_id: contest.id,
        },
      });
    }
    if (contest.slabs) {
      const formatted_slabs = getFormattedSlabs(
        contest.slabs,
        current_slab_index,
        ordered_cx,
        contest.contest_ended,
      );
      list_items.push({
        type: "YOUR_JOURNEY",
        item: {
          current_slab_index: current_slab_index,
          formatted_slabs: formatted_slabs,
          last_processed_time: last_processed_time,
          actual_slab_count: contest.slabs.length - 1,
          contest_ended: contest.contest_ended,
        },
      });
    }
    if (!contest?.show_task_on_top) {
      if (contest.contest_rules_cms_item_id) {
        list_items.push({
          type: "CONTEST_RULES",
          item: {
            contest_rules_cms_item_id: contest.contest_rules_cms_item_id,
            contest_type: contest.contest_type,
          },
        });
      }
      if (contest.tips_banner) {
        list_items.push({
          type: "TIPS_BANNER",
          item: {
            tips_banner: contest.tips_banner,
          },
        });
      }
    }

    let contest_start_date = null;
    if (contest?.start_time) {
      let currentDateObj = new Date();
      if (dayjs(currentDateObj).isBefore(contest.start_time)) {
        let final_date = Math.abs(
            dayjs
              .duration(dayjs(contest.start_time).diff(dayjs(currentDateObj)))
              .get("days"),
          ),
          time_flag = null;
        if (final_date <= 0) {
          final_date = getTime(contest.start_time);
          time_flag = true;
        }
        contest_start_date = { final_date, time_flag };
      }
    }
    yield put(
      contestAction.getContestDetailsContest.success(
        {
          ordered_cx,
          total_cx,
          current_slab_index,
          contest_result_viewed,
          last_processed_time,
          contest_start_date,
          list_items,
          contest_ended: contest?.contest_ended,
          can_only_view: can_only_view,
          contest_name: contest?.contest_name,
        },
        null,
      ),
    );
    if (!fetched)
      yield put(contestAction.getContestDetailsContest.fetched({}, null));
  } catch (err) {
    yield put(contestAction.getContestDetailsContest.error({ err }, null));
    // yield call(Logger.error, err);
  }
}

function* getMyCustomers({ payload }) {
  try {
    const { contest_id, limit = 10, offset = 0 } = payload || {};
    const { data: localData, api = {} } = yield select(myCustomersSelector);
    const moreCustomerLoading = yield select(moreCustLoadingSelector);
    const { data } = yield call(myCustomers, {
      contestId: contest_id,
      limit,
      offset,
    });
    if (data?.contestLeaderCustomers) {
      const { fetched = false } = api || {};
      if (offset !== 0)
        data.contestLeaderCustomers = [
          ...localData.contestLeaderCustomers,
          ...data.contestLeaderCustomers,
        ];
      yield put(contestAction.getMyCustomersContest.success(data, null));
      if (moreCustomerLoading)
        yield put(contestAction.updateMoreCustomerLoading(false));
      if (!fetched)
        yield put(contestAction.getMyCustomersContest.fetched({}, null));
    }
  } catch (err) {
    yield put(contestAction.getMyCustomersContest.error({ err }, null));
    // yield call(Logger.error, err);
  }
}

function* getContestCxOrdered({ payload }) {
  try {
    const { contest_id, limit = 10, offset = 0 } = payload || {};
    const { data: localData, api = {} } = yield select(
      contestCxOrderedSelector,
    );
    const moreOrderedCustLoading = yield select(moreOrderedCustLoadingSelector);
    const { data } = yield call(contestCxOrdered, {
      contestId: contest_id,
      limit,
      offset,
    });
    if (data?.contestLeaderCustomers) {
      const { fetched = false } = api || {};
      if (offset !== 0)
        data.contestLeaderCustomers = [
          ...localData.contestLeaderCustomers,
          ...data.contestLeaderCustomers,
        ];
      yield put(contestAction.getContestCxOrderedContest.success(data, null));
      if (moreOrderedCustLoading)
        yield put(contestAction.updateMoreOrderedCustLoading(false));
      if (!fetched)
        yield put(contestAction.getContestCxOrderedContest.fetched({}, null));
    }
  } catch (err) {
    yield put(contestAction.getContestCxOrderedContest.error({ err }, null));
    // yield call(Logger.error, err);
  }
}

export default function* () {
  yield all([
    takeLatest(contestType.GET_LIVE_UPCOMING, getLiveUpcoming),
    takeLatest(contestType.GET_PAST_CONTEST, getPastContestData),
    takeLatest(contestType.GET_CONTEST_DETAILS, getContestDetails),
    takeLatest(contestType.GET_MY_CUSTOMERS, getMyCustomers),
    takeLatest(contestType.GET_CONTEST_CX_ORDERED, getContestCxOrdered),
  ]);
}
